<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">
      <!-- Brand logo-->
      <b-link
        class="brand-logo"
        href="/home"
      >
        <h2 class="brand-text text-primary ml-1">
          <img
            class="brand-logo-img"
            src="/logo.png"
          >
        </h2>
      </b-link>

      <!-- Left Text-->
      <b-col
        lg="6"
        class="d-none d-lg-flex align-items-center p-5"
      >
        <div
          class="w-100 d-lg-flex align-items-center justify-content-center px-5"
        >
          <b-img
            fluid
            :src="imgUrl"
            alt="Register V2"
          />
        </div>
      </b-col>

      <!-- Register / OTP -->
      <b-col
        lg="6"
        class="d-flex align-items-center auth-bg px-2 p-lg-5"
      >
        <b-col
          sm="8"
          md="6"
          lg="12"
          class="px-xl-2 mx-auto"
        >
          <!-- ══════ OTP VERIFICATION STEP ══════ -->
          <template v-if="verificationStep">
            <div class="otp-card">
              <div class="otp-icon-wrap">
                <div class="otp-icon">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#38B6FF"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                </div>
              </div>
              <h2 class="otp-title">
                {{ $t('auth.verifyEmail') }}
              </h2>
              <p class="otp-subtitle">
                {{ $t('auth.verificationCodeSent') }}<br>
                <strong>{{ otpEmail }}</strong>
              </p>

              <div class="otp-inputs">
                <input
                  v-for="(d, i) in otpDigits"
                  :key="i"
                  :ref="'otp' + i"
                  v-model="otpDigits[i]"
                  type="text"
                  maxlength="1"
                  class="otp-input"
                  :class="{ 'otp-filled': otpDigits[i] }"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  @input="onOtpInput(i)"
                  @keydown="onOtpKeydown($event, i)"
                  @paste="onOtpPaste($event)"
                >
              </div>

              <p
                v-if="otpError"
                class="otp-error"
              >
                {{ otpError }}
              </p>

              <button
                class="otp-btn"
                :disabled="otpLoading || otpDigits.some(d => !d)"
                @click="verifyCode"
              >
                <span
                  v-if="otpLoading"
                  class="otp-spinner"
                />
                <span v-else>{{ $t('auth.verify') }}</span>
              </button>

              <p class="otp-resend">
                <template v-if="resendCooldown > 0">
                  {{ $t('auth.resendIn') }} <strong>{{ resendCooldown }}s</strong>
                </template>
                <template v-else>
                  {{ $t('auth.notReceived') }}
                  <a
                    href="#"
                    class="otp-resend-link"
                    @click.prevent="resendCode"
                  >{{ $t('auth.resendCode') }}</a>
                </template>
              </p>
            </div>
          </template>

          <!-- ══════ REGISTER FORM ══════ -->
          <template v-else>
            <b-card-title
              title-tag="h2"
              class="font-weight-bold mb-1"
            >
              {{ $t('auth.registerTutor') }}
            </b-card-title>
            <b-card-text class="mb-2">
              {{ $t('auth.startNewExperience') }}
            </b-card-text>

            <validation-observer ref="registerForm">
              <b-form class="auth-register-form mt-2">
                <b-row class="m-0">
                  <b-col
                    lg="6"
                    class="p-0"
                  >
                    <b-form-group
                      :label="$t('auth.lastName')"
                      label-for="register-lastname"
                      class="mr-1"
                    >
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('auth.lastName')"
                        rules="required"
                      >
                        <b-form-input
                          id="register-lastname"
                          v-model="model.lastName"
                          name="register-lastname"
                          :state="errors.length > 0 ? false : null"
                          :placeholder="$t('auth.yourLastName')"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col
                    lg="6"
                    class="p-0"
                  >
                    <b-form-group
                      :label="$t('auth.firstName')"
                      label-for="register-firstname"
                      class="ml-1"
                    >
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('auth.firstName')"
                        rules="required"
                      >
                        <b-form-input
                          id="register-firstname"
                          v-model="model.firstName"
                          name="register-firstname"
                          :state="errors.length > 0 ? false : null"
                          :placeholder="$t('auth.yourFirstName')"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-row class="m-0">
                  <b-col
                    lg="6"
                    class="p-0"
                  >
                    <b-form-group
                      :label="$t('auth.email')"
                      label-for="register-email"
                      class="mr-1"
                    >
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('auth.email')"
                        rules="required|email"
                      >
                        <b-form-input
                          id="register-email"
                          v-model="model.email"
                          name="register-email"
                          :state="errors.length > 0 ? false : null"
                          placeholder="name@example.com"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col
                    lg="6"
                    class="p-0"
                  >
                    <b-form-group
                      :label="$t('auth.phone')"
                      label-for="register-phone"
                      class="ml-1"
                    >
                      <vue-tel-input
                        v-model="model.phone"
                        :all-countries="telAllCountries"
                        :preferred-countries="['FR', 'MC', 'TN', 'MA', 'DZ']"
                        :input-options="{ placeholder: $t('auth.phone'), styleClasses: 'form-control' }"
                        mode="international"
                        valid-characters-only
                        class="tel-input-wrapper"
                        :class="{ 'is-invalid-tel': phoneError }"
                        @validate="onPhoneValidate"
                      />
                      <small
                        v-if="phoneError"
                        class="text-danger"
                      >{{ $t('auth.invalidPhone') }}</small>
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-form-group
                  label-for="register-password"
                  :label="$t('auth.password')"
                >
                  <validation-provider
                    #default="{ errors }"
                    :name="$t('auth.password')"
                    rules="required"
                  >
                    <b-input-group
                      class="input-group-merge"
                      :class="errors.length > 0 ? 'is-invalid' : null"
                    >
                      <b-form-input
                        id="register-password"
                        v-model="model.password"
                        class="form-control-merge"
                        :type="passwordFieldType"
                        :state="errors.length > 0 ? false : null"
                        name="register-password"
                        placeholder="············"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          :icon="passwordToggleIcon"
                          class="cursor-pointer"
                          @click="togglePasswordVisibility"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>

                <b-row class="m-0">
                  <b-col
                    lg="6"
                    class="p-0"
                  >
                    <b-form-group
                      :label="$t('auth.birthDate')"
                      label-for="register-birthdate"
                      class="mr-1"
                    >
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('auth.birthDate')"
                        rules="required"
                      >
                        <flat-pickr
                          id="register-date"
                          v-model="model.birthdate"
                          class="form-control"
                          name="register-birthdate"
                          :placeholder="$t('auth.birthDate')"
                          :config="{
                            maxDate: moment().subtract(18, 'years').format('YYYY-MM-DD')
                          }"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col
                    lg="6"
                    class="p-0"
                  >
                    <b-form-group
                      :label="$t('auth.address')"
                      label-for="register-address"
                      class="ml-1"
                    >
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('auth.address')"
                        rules="required"
                      >
                        <b-form-input
                          id="register-address"
                          v-model="model.address"
                          name="register-address"
                          :state="errors.length > 0 ? false : null"
                          :placeholder="$t('auth.yourAddress')"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-row class="m-0">
                  <b-col
                    lg="6"
                    class="p-0"
                  >
                    <b-form-group
                      :label="$t('auth.linkedin')"
                      label-for="register-linkedin"
                      class="mr-1"
                    >
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('auth.linkedin')"
                        :rules="{required:true,
                                 regex: /(https?:\/\/(www.)|(www.))?linkedin.com\/(mwlite\/|m\/)?in\/[a-zA-Z0-9_.-]+\/?/}"
                      >
                        <b-form-input
                          id="register-linkedin"
                          v-model="model.linkedin"
                          name="register-linkedin"
                          :state="errors.length > 0 ? false : null"
                          :placeholder="$t('auth.linkedin')"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col
                    lg="6"
                    class="p-0"
                  >
                    <b-form-group
                      :label="$t('auth.institutionCompany')"
                      label-for="register-university"
                      class="ml-1"
                    >
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('auth.institutionCompany')"
                        rules="required"
                      >
                        <b-form-input
                          id="register-university"
                          v-model="model.school"
                          name="register-university"
                          :state="errors.length > 0 ? false : null"
                          :placeholder="$t('auth.institutionName')"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-row class="m-0">
                  <b-col
                    lg="12"
                    class="p-0"
                  >
                    <b-form-group
                      :label="$t('auth.cv')"
                      label-for="Cv"
                    >
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('auth.cv')"
                        rules="required"
                      >
                        <b-form-file
                          v-model="model.cv"
                          name="Cv"
                          :state="errors.length > 0 ? false : null"
                          :placeholder="$t('auth.chooseFile')"
                          :drop-placeholder="$t('auth.dropFile')"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-button
                  variant="primary"
                  block
                  type="submit"
                  @click.prevent="validationForm"
                >
                  {{ $t('auth.register') }}
                </b-button>
              </b-form>
            </validation-observer>

            <p class="text-center mt-2">
              <span>{{ $t('auth.alreadyHaveAccount') }}</span>
              <b-link :to="{ name: 'login-tutor' }">
                <span>&nbsp;{{ $t('auth.login') }}</span>
              </b-link>
            </p>
          </template>
        </b-col>
      </b-col>
    </b-row>
  </div>
</template>

<script>
/* eslint-disable global-require */
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import VuexyLogo from '@core/layouts/components/Logo.vue'
import {
  BRow,
  BCol,
  BLink,
  BButton,
  BForm,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormFile,
  BFormDatepicker,
  BInputGroup,
  BInputGroupAppend,
  BImg,
  BCardTitle,
  BCardText,
} from 'bootstrap-vue'
import {
  required, email, regex,
} from '@validations'
import { VueTelInput } from 'vue-tel-input'
import 'vue-tel-input/dist/vue-tel-input.css'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import flatPickr from 'vue-flatpickr-component'
import store from '@/store/index'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import * as moment from 'moment'

const stripNonLatinParentheses = name => {
  if (!name) return name
  const cleaned = name.replace(/\(([^)]*)\)/g, (full, inner) => {
    for (const ch of inner) {
      if (ch.codePointAt(0) > 0x024F) return ''
    }
    return full
  })

  return cleaned
    .replace(/\s{2,}/g, ' ')
    .replace(/\(\s+/g, '(')
    .replace(/\s+\)/g, ')')
    .trim()
}

const TEL_ALL_COUNTRIES_SANITIZED = VueTelInput?.props?.allCountries?.default
  ? VueTelInput.props.allCountries.default().map(c => ({ ...c, name: stripNonLatinParentheses(c.name) }))
  : undefined

export default {
  components: {
    VuexyLogo,
    BRow,
    BImg,
    BCol,
    BLink,
    BButton,
    BForm,
    BCardText,
    BCardTitle,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormFile,
    BFormDatepicker,
    BInputGroup,
    BInputGroupAppend,
    ValidationProvider,
    ValidationObserver,
    flatPickr,
    VueTelInput,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      model: {
        lastName: '',
        firstName: '',
        email: '',
        phone: '',
        password: '',
        birthdate: '',
        address: '',
        linkedin: '',
        school: '',
        cv: null,
      },
      phoneError: false,
      phoneValid: false,
      verificationStep: false,
      otpEmail: '',
      otpDigits: ['', '', '', ''],
      otpLoading: false,
      otpError: '',
      resendCooldown: 0,
      resendTimer: null,

      sideImg: require('@/assets/images/pages/register-v2.svg'),
      telAllCountries: TEL_ALL_COUNTRIES_SANITIZED,
      required,
      email,
      regex,
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    imgUrl() {
      if (store.state.appConfig.layout.skin === 'dark') {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.sideImg = require('@/assets/images/pages/register-v2-dark.svg')
        return this.sideImg
      }
      return this.sideImg
    },
  },
  mounted() {
    if (this.$route.query.verify) {
      this.otpEmail = this.$route.query.verify
      this.verificationStep = true
      this.startResendCooldown()
    }
  },
  beforeDestroy() {
    if (this.resendTimer) clearInterval(this.resendTimer)
  },
  methods: {
    moment,
    onPhoneValidate(validation) {
      this.phoneValid = validation.valid
      if (this.model.phone && this.model.phone.length > 3) {
        this.phoneError = !validation.valid
      }
    },
    validationForm() {
      this.$refs.registerForm.validate().then(success => {
        if (!success) return
        if (!this.phoneValid) {
          this.phoneError = true
          return
        }

        const generatedUsername = `${this.model.email.split('@')[0]}_${Math.random().toString(36).substr(2, 4)}`
        const fullName = `${this.model.firstName} ${this.model.lastName}`

        const formData = new FormData()
        formData.append('username', generatedUsername)
        formData.append('fullName', fullName)
        formData.append('email', this.model.email)
        formData.append('phone', this.model.phone)
        formData.append('password', this.model.password)
        formData.append('birthdate', this.model.birthdate)
        formData.append('school', this.model.school)
        formData.append('address', this.model.address)
        formData.append('linkedin', this.model.linkedin)

        if (!this.model.cv) {
          return this.$toast({ component: ToastificationContent, props: { title: this.$t('auth.uploadCV'), icon: 'XIcon', variant: 'danger' } })
        }
        if (this.model.cv.size > 1000000) {
          return this.$toast({ component: ToastificationContent, props: { title: this.$t('auth.cvTooLarge'), icon: 'XIcon', variant: 'danger' } })
        }

        formData.append('cv', this.model.cv)

        this.$http.post('/tutor/signup', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
          .then(res => {
            if (res.data && res.data.needVerification) {
              this.otpEmail = res.data.email || this.model.email
              this.verificationStep = true
              this.startResendCooldown()
              this.$toast({ component: ToastificationContent, props: { title: this.$t('auth.codeSentByEmail'), icon: 'MailIcon', variant: 'success' } })
            } else {
              this.goToTutorLogin()
            }
          })
          .catch(e => {
            if (e.response && e.response.data && e.response.data.error === 'email') {
              return this.$toast({ component: ToastificationContent, props: { title: this.$t('auth.emailAlreadyUsed'), icon: 'XIcon', variant: 'danger' } })
            }
            if (e.response && e.response.data && e.response.data.error === 'username') {
              return this.$toast({ component: ToastificationContent, props: { title: this.$t('auth.usernameAlreadyUsed'), icon: 'XIcon', variant: 'danger' } })
            }
            return this.$toast({ component: ToastificationContent, props: { title: this.$t('auth.errorOccurred'), icon: 'XIcon', variant: 'danger' } })
          })
      })
    },
    // Signing up from the Classroom add-on must return to the account-link
    // bridge instead of the regular dashboard entry point.
    goToTutorLogin() {
      const path = this.$route.query.addonLink === '1'
        ? '/login/tutor?addonLink=1'
        : '/login/tutor'
      this.$router.push(path)
    },
    onOtpInput(idx) {
      this.otpError = ''
      const val = this.otpDigits[idx].replace(/\D/g, '')
      this.$set(this.otpDigits, idx, val)
      if (val && idx < 3) {
        this.$nextTick(() => {
          const next = this.$refs[`otp${idx + 1}`]
          if (next && next[0]) next[0].focus()
        })
      }
      if (this.otpDigits.every(d => d)) {
        this.verifyCode()
      }
    },
    onOtpKeydown(e, idx) {
      if (e.key === 'Backspace' && !this.otpDigits[idx] && idx > 0) {
        this.$set(this.otpDigits, idx - 1, '')
        this.$nextTick(() => {
          const prev = this.$refs[`otp${idx - 1}`]
          if (prev && prev[0]) prev[0].focus()
        })
      }
    },
    onOtpPaste(e) {
      e.preventDefault()
      const paste = (e.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '').slice(0, 4)
      for (let i = 0; i < 4; i++) {
        this.$set(this.otpDigits, i, paste[i] || '')
      }
      if (paste.length === 4) this.verifyCode()
    },
    async verifyCode() {
      const code = this.otpDigits.join('')
      if (code.length !== 4) return
      this.otpLoading = true
      this.otpError = ''
      try {
        await this.$http.post('/tutor/verify-email', { email: this.otpEmail, code })
        this.$toast({ component: ToastificationContent, props: { title: this.$t('auth.emailVerified'), icon: 'CheckCircleIcon', variant: 'success' } })
        this.goToTutorLogin()
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.error) || this.$t('auth.verificationError')
        this.otpError = msg
        this.otpDigits = ['', '', '', '']
        this.$nextTick(() => {
          const first = this.$refs.otp0
          if (first && first[0]) first[0].focus()
        })
      } finally {
        this.otpLoading = false
      }
    },
    async resendCode() {
      if (this.resendCooldown > 0) return
      try {
        await this.$http.post('/tutor/resend-code', { email: this.otpEmail })
        this.$toast({ component: ToastificationContent, props: { title: this.$t('auth.newCodeSent'), icon: 'MailIcon', variant: 'success' } })
        this.startResendCooldown()
      } catch (err) {
        this.$toast({ component: ToastificationContent, props: { title: this.$t('auth.resendError'), icon: 'XIcon', variant: 'danger' } })
      }
    },
    startResendCooldown() {
      this.resendCooldown = 60
      if (this.resendTimer) clearInterval(this.resendTimer)
      this.resendTimer = setInterval(() => {
        this.resendCooldown--
        if (this.resendCooldown <= 0) clearInterval(this.resendTimer)
      }, 1000)
    },
  },
}
/* eslint-disable global-require */
</script>

<style lang="scss">
@import "@core/scss/vue/pages/page-auth.scss";
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>

<style scoped>
.otp-card {
  text-align: center;
  padding: 20px 0;
}
.otp-icon-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}
.otp-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e8f6ff, #d0edff);
  display: flex;
  align-items: center;
  justify-content: center;
}
.otp-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 8px;
}
.otp-subtitle {
  color: #64748b;
  font-size: 0.95rem;
  margin-bottom: 28px;
  line-height: 1.5;
}
.otp-subtitle strong {
  color: #38B6FF;
}
.otp-inputs {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 20px;
}
.otp-input {
  width: 56px;
  height: 64px;
  text-align: center;
  font-size: 1.8rem;
  font-weight: 800;
  color: #0884D0;
  border: 2px solid #e0e7ef;
  border-radius: 14px;
  background: #f8fafc;
  outline: none;
  transition: all 0.25s;
  caret-color: #38B6FF;
}
.otp-input:focus {
  border-color: #38B6FF;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(56, 182, 255, 0.15);
}
.otp-input.otp-filled {
  border-color: #38B6FF;
  background: #e8f6ff;
}
.otp-error {
  color: #e53e3e;
  font-size: 0.85rem;
  margin-bottom: 16px;
  font-weight: 600;
}
.otp-btn {
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #38B6FF, #0884D0);
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.otp-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(56, 182, 255, 0.35);
}
.otp-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.otp-spinner {
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.otp-resend {
  color: #64748b;
  font-size: 0.88rem;
}
.otp-resend strong {
  color: #38B6FF;
}
.otp-resend-link {
  color: #38B6FF;
  font-weight: 600;
  text-decoration: none;
}
.otp-resend-link:hover {
  text-decoration: underline;
}

/* vue-tel-input styling */
.tel-input-wrapper {
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;
  background: #fff;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}
.tel-input-wrapper:focus-within {
  border-color: #38B6FF;
  box-shadow: 0 0 0 3px rgba(56, 182, 255, 0.15);
}
.is-invalid-tel {
  border-color: #ea5455 !important;
}
.is-invalid-tel:focus-within {
  box-shadow: 0 0 0 3px rgba(234, 84, 85, 0.15) !important;
}
</style>
<style>
.tel-input-wrapper .vti__input {
  border: none !important;
  box-shadow: none !important;
  padding: 0.438rem 1rem;
  font-size: 1rem;
  line-height: 1.45;
  color: #6e6b7b;
  background: transparent;
}
.tel-input-wrapper .vti__dropdown {
  border-right: 1px solid #d8d6de;
  padding: 4px 8px;
  background: #f8f8f8;
  border-radius: 0.357rem 0 0 0.357rem;
}
.tel-input-wrapper .vti__dropdown:hover {
  background: #eee;
}
.tel-input-wrapper .vti__dropdown-list {
  z-index: 999;
  border: 1px solid #d8d6de;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  max-height: 200px;
}
.brand-logo-img { height: 80px; }
@media (max-width: 768px) {
  .brand-logo-img { height: 50px; }
}
</style>
